import React, { useState, useEffect } from "react";
import {
  Card,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ImageList,
  ImageListItem,
  Chip,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import VuiPagination from "components/VuiPagination";
import Icon from "@mui/material/Icon";
import { green, grey } from "@mui/material/colors";
import { useParams, useNavigate } from "react-router-dom";

function BreakdownTracker() {
  const { site_id, section_id } = useParams();
  const navigate = useNavigate();
  const [sectionName, setSectionName] = useState("");
  const [breakdowns, setBreakdowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [pictureDialogOpen, setPictureDialogOpen] = useState(false);
  const [currentBreakdown, setCurrentBreakdown] = useState(null);
  const [taskCompletions, setTaskCompletions] = useState({});
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [currentPhotos, setCurrentPhotos] = useState([]);
  const [siteName, setSiteName] = useState("");
  const [newTaskItems, setNewTaskItems] = useState([""]);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [currentComments, setCurrentComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [editingPart, setEditingPart] = useState(null);
  const [editingTaskItem, setEditingTaskItem] = useState(null);
  const [editedTaskItemText, setEditedTaskItemText] = useState("");
  const [newTaskItemText, setNewTaskItemText] = useState("");
  const [newItem, setNewItem] = useState({
    breakdown_name: "",
    breakdown_description: "",
    hours_usage: 0,
    maintenance_interval: 0,
    breakdown_items: [],
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editingField, setEditingField] = useState({ id: null, field: null });
  const [partsDialogOpen, setPartsDialogOpen] = useState(false);
  const [currentParts, setCurrentParts] = useState([]);
  const [newPart, setNewPart] = useState({
    part_name: "",
    part_description: "",
    quantity: 1,
  });

  const email = localStorage.getItem("email");
  const userName = localStorage.getItem("name");
  const token = localStorage.getItem("token");

  // Helper function to make authenticated requests
  const makeAuthenticatedRequest = async (url, options = {}) => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `${token}`,
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    return response.json();
  };

  // Fetch site data
  const fetchSiteData = async () => {
    try {
      // Fetch site name
      const siteData = await makeAuthenticatedRequest(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/site/profile?site_id=${site_id}`
      );
      setSiteName(siteData.name);

      // Fetch section name if section_id exists
      if (section_id) {
        const sectionData = await makeAuthenticatedRequest(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/sections/profile?section_id=${section_id}`
        );
        setSectionName(sectionData.section_name);
      }
    } catch (error) {
      console.error("Error fetching site/section data:", error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch breakdowns
  const fetchBreakdowns = async () => {
    setLoading(true);
    try {
      let url = `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/sites/breakdowns?site_id=${site_id}`;
      if (section_id) {
        url = `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/sections/breakdowns?section_id=${section_id}`;
      }

      const data = await makeAuthenticatedRequest(url);

      const transformedData = data.map((breakdown) => ({
        id: breakdown.breakdown_id,
        item: breakdown.breakdown_name,
        hours_usage: breakdown.hours_usage,
        maintenance_interval: breakdown.maintenance_interval,
        description: breakdown.breakdown_description,
        status: breakdown.status,
        created_at: breakdown.created_at,
        completed_at: breakdown.completed_at,
        items: breakdown.items || [],
        photo_count: breakdown.photo_count || 0,
        part_count: breakdown.part_count || 0,
        comment_count: breakdown.comment_count || 0,
        breakdownitem_count: breakdown.breakdownitem_count || 0,
        completed_items: breakdown.completed_items || 0,
      }));

      setBreakdowns(transformedData);
    } catch (error) {
      console.error("Error fetching breakdowns:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSiteData();
    fetchBreakdowns();
  }, [site_id, section_id]);

  // Handle field edit
  const handleFieldEdit = (id, field) => {
    setEditingField({ id, field });
  };

  // Handle field update
  const handleFieldUpdate = async (id, field, value) => {
    try {
      await makeAuthenticatedRequest(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/update`,
        {
          method: "PUT",
          body: JSON.stringify({
            breakdown_id: id,
            [field]: Number(value),
          }),
        }
      );

      await fetchBreakdowns();
    } catch (error) {
      console.error("Error updating breakdown:", error);
    } finally {
      setEditingField({ id: null, field: null });
    }
  };

  // Handle complete breakdown
  const handleCompleteBreakdown = async (breakdownId, currentStatus) => {
    try {
      const newStatus = currentStatus === "completed" ? "pending" : "completed";
      console.log('Updating breakdown status:', { breakdownId, newStatus });
  
      const response = await makeAuthenticatedRequest(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/update",
        {
          method: "PUT",
          body: JSON.stringify({ breakdown_id: breakdownId, status: newStatus }),
        }
      );
  
      console.log('Update successful:', response);
      await fetchBreakdowns();
    } catch (error) {
      console.error("Error updating breakdown status:", error);
    }
  };

  // Handle delete breakdown
  const handleDeleteBreakdown = async (breakdownId) => {
    if (window.confirm("Are you sure you want to delete this breakdown?")) {
      try {
        await makeAuthenticatedRequest(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/delete?breakdown_id=${breakdownId}`,
          {
            method: "DELETE",
          }
        );
        fetchBreakdowns();
      } catch (error) {
        console.error("Error deleting breakdown:", error);
      }
    }
  };

  // Handle add breakdown
  const handleAddItem = async () => {
    try {
      const filteredBreakdownItems = newTaskItems.filter((item) => item.trim() !== "");

      await makeAuthenticatedRequest(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/sites/breakdowns/create?site_id=${site_id}`,
        {
          method: "POST",
          body: JSON.stringify({
            breakdown_name: newItem.breakdown_name,
            breakdown_description: newItem.breakdown_description,
            hours_usage: Number(newItem.hours_usage),
            maintenance_interval: Number(newItem.maintenance_interval),
            breakdown_items: filteredBreakdownItems,
          }),
        }
      );

      // If section_id exists, assign the breakdown to the section
      if (section_id) {
        await makeAuthenticatedRequest(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/sections/breakdowns/assign`,
          {
            method: "POST",
            body: JSON.stringify({
              section_id: section_id,
              breakdown_id: breakdownData.breakdown_id,
            }),
          }
        );
      }

      setAddDialogOpen(false);
      await fetchBreakdowns();
      setNewItem({
        breakdown_name: "",
        breakdown_description: "",
        hours_usage: 0,
        maintenance_interval: 0,
      });
      setNewTaskItems([""]);
    } catch (error) {
      console.error("Error creating breakdown:", error);
    }
  };

  // Handle add breakdown item
  const handleAddBreakdownItem = async () => {
    if (!currentBreakdown || !newTaskItemText.trim()) return;

    try {
      const newBreakdownItem = await makeAuthenticatedRequest(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdown-items/add`,
        {
          method: "POST",
          body: JSON.stringify({
            breakdown_id: currentBreakdown.id,
            item_text: newTaskItemText,
          }),
        }
      );

      setCurrentBreakdown((prev) => ({
        ...prev,
        items: [...prev.items, newBreakdownItem],
      }));

      setNewTaskItemText("");

      setBreakdowns((prevItems) =>
        prevItems.map((item) =>
          item.id === currentBreakdown.id
            ? { ...item, breakdownitem_count: (item.breakdownitem_count || 0) + 1 }
            : item
        )
      );
    } catch (error) {
      console.error("Error adding breakdown item:", error);
    }
  };

  // Handle upload photo
  const handleUploadPhoto = async (event) => {
    const file = event.target.files[0];
    if (!file || !currentBreakdown) return;

    setUploadingPhoto(true);
    try {
      const { upload_url, photo_id, s3_key } = await makeAuthenticatedRequest(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/photos/generate-presigned-url",
        {
          method: "POST",
          body: JSON.stringify({
            breakdown_id: currentBreakdown.id,
            site_id: site_id,
            file_name: file.name,
            file_type: file.type || "image/jpeg",
          }),
        }
      );

      const uploadResult = await fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      if (!uploadResult.ok) throw new Error("Upload failed");

      const newPhoto = await makeAuthenticatedRequest(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/photos/create-photo-record",
        {
          method: "POST",
          body: JSON.stringify({
            photo_id,
            breakdown_id: currentBreakdown.id,
            s3_key,
            file_name: file.name,
          }),
        }
      );

      setCurrentPhotos((prev) => [newPhoto, ...prev]);
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Upload failed: " + error.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await makeAuthenticatedRequest(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/photos/delete`,
        {
          method: "DELETE",
          body: JSON.stringify({ photo_id: photoId }),
        }
      );

      setCurrentPhotos((prevPhotos) => prevPhotos.filter((photo) => photo.photo_id !== photoId));
    } catch (error) {
      console.error("Error deleting photo:", error);
    }
  };

  // Handle add comment
  const handleAddComment = async () => {
    if (!currentBreakdown) {
      console.error("No breakdown selected for comment");
      return;
    }

    try {
      const newFormattedComment = await makeAuthenticatedRequest(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/comments/add",
        {
          method: "POST",
          body: JSON.stringify({
            breakdown_id: currentBreakdown.id,
            comment_text: newComment,
            created_by: userName || email,
          }),
        }
      );

      setCurrentComments([newFormattedComment, ...currentComments]);
      setNewComment("");
      setBreakdowns((prevItems) =>
        prevItems.map((item) =>
          item.id === currentBreakdown.id
            ? { ...item, comment_count: (item.comment_count || 0) + 1 }
            : item
        )
      );
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  // Handle open breakdown dialog
  const handleOpenBreakdownDialog = async (breakdownId) => {
    try {
      const data = await makeAuthenticatedRequest(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdown-items?breakdown_id=${breakdownId}`
      );

      const completions = data.reduce((acc, task) => {
        acc[task.item_id] = task.is_completed;
        return acc;
      }, {});

      const completedItems = data.filter((item) => item.is_completed).length;
      const totalItems = data.length;
      const completionPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

      setCurrentBreakdown({
        id: breakdownId,
        items: data,
        completionPercentage: completionPercentage,
      });

      setTaskCompletions(completions);
      setTaskDialogOpen(true);
    } catch (error) {
      console.error("Error fetching breakdown items:", error);
    }
  };

  // Handle open picture dialog
  const handleOpenPictureDialog = async (itemId) => {
    const item = breakdowns.find((item) => item.id === itemId);
    if (item) {
      setCurrentBreakdown(item);
      setPictureDialogOpen(true);
      try {
        const data = await makeAuthenticatedRequest(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/photos?breakdown_id=${item.id}`
        );
        setCurrentPhotos(data || []);
      } catch (error) {
        console.error("Error fetching photos:", error);
        setCurrentPhotos([]);
      }
    }
  };

  // Handle open parts dialog
  const handleOpenPartsDialog = async (breakdownId) => {
    const item = breakdowns.find((item) => item.id === breakdownId);
    if (item) {
      setCurrentBreakdown(item);
      setPartsDialogOpen(true);
      try {
        const data = await makeAuthenticatedRequest(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/parts?breakdown_id=${item.id}`
        );
        setCurrentParts(data || []);
      } catch (error) {
        console.error("Error fetching parts:", error);
        setCurrentParts([]);
      }
    }
  };

  // Handle add part
  const handleAddPart = async () => {
    try {
      const data = await makeAuthenticatedRequest(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/parts/add",
        {
          method: "POST",
          body: JSON.stringify({
            breakdown_id: currentBreakdown.id,
            ...newPart,
          }),
        }
      );

      setCurrentParts((prevParts) => [data, ...prevParts]);

      setNewPart({
        part_name: "",
        part_description: "",
        quantity: 1,
      });
    } catch (error) {
      console.error("Error adding part:", error);
      alert("Failed to add part: " + error.message);
    }
  };

  // Handle delete part
  const handleDeletePart = async (partId) => {
    try {
      await makeAuthenticatedRequest(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/parts/delete`,
        {
          method: "DELETE",
          body: JSON.stringify({
            part_id: partId,
          }),
        }
      );
      setCurrentParts((prev) => prev.filter((part) => part.part_id !== partId));
    } catch (error) {
      console.error("Error deleting part:", error);
    }
  };

  // Handle update part
  const handleUpdatePart = async () => {
    try {
      await makeAuthenticatedRequest(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/parts/update",
        {
          method: "PUT",
          body: JSON.stringify({
            part_id: editingPart.part_id,
            part_name: editingPart.part_name,
            part_description: editingPart.part_description,
            quantity: editingPart.quantity,
          }),
        }
      );

      setCurrentParts((prev) =>
        prev.map((part) => (part.part_id === editingPart.part_id ? editingPart : part))
      );
      setEditingPart(null);
    } catch (error) {
      console.error("Error updating part:", error);
    }
  };

  const handleOpenCommentsDialog = async (itemId) => {
    const item = breakdowns.find((item) => item.id === itemId);
    if (item) {
      setCurrentBreakdown(item);
      setCommentsDialogOpen(true);
      try {
        const data = await makeAuthenticatedRequest(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/comments?breakdown_id=${item.id}`
        );
        setCurrentComments(data || []);
      } catch (error) {
        console.error("Error fetching comments:", error);
        setCurrentComments([]);
      }
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await makeAuthenticatedRequest(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdowns/comments/delete`,
        {
          method: "DELETE",
          body: JSON.stringify({
            comment_id: commentId,
          }),
        }
      );

      setCurrentComments((prev) => prev.filter((comment) => comment.comment_id !== commentId));

      if (currentBreakdown) {
        setBreakdowns((prevItems) =>
          prevItems.map((item) =>
            item.id === currentBreakdown.id && item.comment_count > 0
              ? { ...item, comment_count: item.comment_count - 1 }
              : item
          )
        );
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const handleEditBreakdownItem = (breakdownId, itemId, itemText) => {
    setEditingTaskItem(itemId);
    setEditedTaskItemText(itemText);
  };

  const handleEditPart = (part) => {
    setEditingPart(part);
  };
  const handleCancelEdit = () => {
    setEditingPart(null);
  };
  const handleSaveBreakdownItem = async (breakdownId, itemId) => {
    try {
      await makeAuthenticatedRequest(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdown-items/update`,
        {
          method: "PUT",
          body: JSON.stringify({
            item_id: itemId,
            item_text: editedTaskItemText,
          }),
        }
      );

      setCurrentBreakdown((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.item_id === itemId ? { ...item, item_text: editedTaskItemText } : item
        ),
      }));

      setEditingTaskItem(null);
      setEditedTaskItemText("");
    } catch (error) {
      console.error("Error updating breakdown item:", error);
    }
  };

  const handleDeleteBreakdownItem = async (breakdownId, itemId) => {
    if (window.confirm("Are you sure you want to delete this breakdown item?")) {
      try {
        await makeAuthenticatedRequest(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdown-items/delete`,
          {
            method: "DELETE",
            body: JSON.stringify({
              item_id: itemId,
            }),
          }
        );

        setCurrentBreakdown((prev) => ({
          ...prev,
          items: prev.items.filter((item) => item.item_id !== itemId),
        }));

        if (currentBreakdown) {
          setBreakdowns((prevItems) =>
            prevItems.map((item) =>
              item.id === currentBreakdown.id && item.item_count > 0
                ? { ...item, item_count: item.item_count - 1 }
                : item
            )
          );
        }
      } catch (error) {
        console.error("Error deleting breakdown item:", error);
      }
    }
  };

  const handleToggleBreakdownItemCompletion = async (breakdownId, itemId, isCompleted) => {
    try {
      await makeAuthenticatedRequest(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/breakdown-items/update`,
        {
          method: "PUT",
          body: JSON.stringify({
            item_id: itemId,
            is_completed: !isCompleted,
          }),
        }
      );

      setTaskCompletions((prev) => ({
        ...prev,
        [itemId]: !isCompleted,
      }));

      setCurrentBreakdown((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.item_id === itemId ? { ...item, is_completed: !isCompleted } : item
        ),
      }));

      setBreakdowns((prevItems) =>
        prevItems.map((item) =>
          item.id === breakdownId
            ? {
                ...item,
                completed_items: item.completed_items + (isCompleted ? -1 : 1),
              }
            : item
        )
      );
    } catch (error) {
      console.error("Error updating breakdown item completion:", error);
    }
  };

  // Columns for the table
  const columns = [
    { name: "Breakdown", align: "left" },
    { name: "Hours", align: "center" },
    { name: "Maintenance Interval", align: "center" },
    { name: "Completion", align: "center" },
    { name: "Comments", align: "center" },
    { name: "Checklist", align: "center" },
    { name: "Pictures", align: "center" },
    { name: "Parts", align: "center" },
    { name: "Actions", align: "center" },
  ];

  // Rows for the table
  const rows = breakdowns.slice((page - 1) * rowsPerPage, page * rowsPerPage).map((item) => {
    const completion =
      (item.breakdownitem_count || 0) > 0
        ? `${Math.round(((item.completed_items || 0) / item.breakdownitem_count) * 100)}%`
        : "N/A";

    return {
      Breakdown: item.item,
      Hours:
        editingField.id === item.id && editingField.field === "hours_usage" ? (
          <TextField
            type="number"
            defaultValue={item.hours_usage}
            onBlur={(e) => handleFieldUpdate(item.id, "hours_usage", e.target.value)}
            autoFocus
          />
        ) : (
          <span onClick={() => handleFieldEdit(item.id, "hours_usage")}>{item.hours_usage}</span>
        ),
      "Maintenance Interval":
        editingField.id === item.id && editingField.field === "maintenance_interval" ? (
          <TextField
            type="number"
            defaultValue={item.maintenance_interval}
            onBlur={(e) => handleFieldUpdate(item.id, "maintenance_interval", e.target.value)}
            autoFocus
          />
        ) : (
          <span onClick={() => handleFieldEdit(item.id, "maintenance_interval")}>
            {item.maintenance_interval}
          </span>
        ),
      Completion: completion,
      Comments: (
        <VuiButton color="info" onClick={() => handleOpenCommentsDialog(item.id)}>
          {item.comment_count || 0} Comments
        </VuiButton>
      ),
      Checklist: (
        <VuiButton color="info" onClick={() => handleOpenBreakdownDialog(item.id)}>
          {item.breakdownitem_count || 0} Items
        </VuiButton>
      ),
      Pictures: (
        <VuiButton color="info" onClick={() => handleOpenPictureDialog(item.id)}>
          {item.photo_count || 0} Photos
        </VuiButton>
      ),
      Parts: (
        <VuiButton color="info" onClick={() => handleOpenPartsDialog(item.id)}>
          {item.part_count || 0} Parts
        </VuiButton>
      ),
      Actions: (
        <>
          <Checkbox
            checked={item.status === "completed"}
            onChange={() => handleCompleteBreakdown(item.id, item.status)}
            color="success"
            disableRipple
            sx={{
              border: "1px solid grey",
              borderRadius: "5px",
              "&.Mui-checked": {
                border: "1px solid green",
              },
            }}
          />
          <IconButton color="error" onClick={() => handleDeleteBreakdown(item.id)}>
            <Icon>delete</Icon>
          </IconButton>
        </>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
            <VuiTypography variant="lg" color="white">
              Breakdowns - {sectionName || siteName}
            </VuiTypography>
            <VuiButton variant="contained" color="primary" onClick={() => setAddDialogOpen(true)}>
              <Icon>add</Icon>&nbsp; Add Breakdown
            </VuiButton>
          </VuiBox>

          {loading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </VuiBox>
          ) : (
            <Table columns={columns} rows={rows} />
          )}

          {breakdowns.length > 0 && (
            <VuiBox display="flex" justifyContent="center" p={2}>
              <VuiPagination>
                <VuiPagination
                  item
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  <Icon>keyboard_arrow_left</Icon>
                </VuiPagination>
                {Array.from({ length: Math.ceil(breakdowns.length / rowsPerPage) }, (_, i) => (
                  <VuiPagination
                    key={i + 1}
                    item
                    active={page === i + 1}
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </VuiPagination>
                ))}
                <VuiPagination
                  item
                  onClick={() =>
                    setPage(Math.min(Math.ceil(breakdowns.length / rowsPerPage), page + 1))
                  }
                  disabled={page === Math.ceil(breakdowns.length / rowsPerPage)}
                >
                  <Icon>keyboard_arrow_right</Icon>
                </VuiPagination>
              </VuiPagination>
            </VuiBox>
          )}
        </Card>
      </VuiBox>

      {/* Add Breakdown Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Breakdown</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Breakdown Name"
                margin="dense"
                value={newItem.breakdown_name}
                onChange={(e) => setNewItem({ ...newItem, breakdown_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Hours/Usage"
                margin="dense"
                value={newItem.hours_usage}
                onChange={(e) => setNewItem({ ...newItem, hours_usage: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Maintenance Interval"
                margin="dense"
                value={newItem.maintenance_interval}
                onChange={(e) => setNewItem({ ...newItem, maintenance_interval: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Breakdown Description"
                margin="dense"
                value={newItem.breakdown_description}
                onChange={(e) => setNewItem({ ...newItem, breakdown_description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <VuiTypography variant="subtitle2" mt={2} mb={1}>
                Breakdown Checklist Items
              </VuiTypography>
              {newTaskItems.map((item, index) => (
                <Grid container spacing={1} key={index} alignItems="center" mb={1}>
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      size="small"
                      label={`Checklist Item ${index + 1}`}
                      value={item}
                      onChange={(e) => {
                        const updatedItems = [...newTaskItems];
                        updatedItems[index] = e.target.value;
                        setNewTaskItems(updatedItems);
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      color="error"
                      onClick={() => {
                        const updatedItems = newTaskItems.filter((_, i) => i !== index);
                        setNewTaskItems(updatedItems);
                      }}
                      disabled={newTaskItems.length === 1}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Icon>add</Icon>}
                onClick={() => setNewTaskItems([...newTaskItems, ""])}
                fullWidth
                sx={{ mt: 1 }}
              >
                Add Checklist Item
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleAddItem}
            color="primary"
            disabled={!newItem.breakdown_name || newTaskItems.every((item) => item.trim() === "")}
          >
            Create Breakdown
          </Button>
        </DialogActions>
      </Dialog>

      {/* Breakdown Checklist Dialog */}
      <Dialog
        open={taskDialogOpen}
        onClose={() => setTaskDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{currentBreakdown?.item} Checklist</DialogTitle>
        <DialogContent>
          {currentBreakdown?.description && (
            <VuiBox mb={3}>
              <VuiTypography variant="body2" color="text">
                {currentBreakdown.description}
              </VuiTypography>
            </VuiBox>
          )}
          <List>
            {currentBreakdown?.items && currentBreakdown.items.length > 0 ? (
              currentBreakdown.items.map((task) => (
                <ListItem key={task.item_id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!taskCompletions[task.item_id]}
                        onChange={() =>
                          handleToggleBreakdownItemCompletion(
                            currentBreakdown.id,
                            task.item_id,
                            taskCompletions[task.item_id]
                          )
                        }
                        color="primary"
                      />
                    }
                    label={
                      editingTaskItem === task.item_id ? (
                        <TextField
                          fullWidth
                          value={editedTaskItemText}
                          onChange={(e) => setEditedTaskItemText(e.target.value)}
                          onBlur={() => handleSaveBreakdownItem(currentBreakdown.id, task.item_id)}
                          autoFocus
                        />
                      ) : (
                        <ListItemText
                          primary={task.item_text}
                          style={{
                            textDecoration: taskCompletions[task.item_id] ? "line-through" : "none",
                          }}
                        />
                      )
                    }
                  />
                  <IconButton
                    onClick={() =>
                      handleEditBreakdownItem(currentBreakdown.id, task.item_id, task.item_text)
                    }
                    color="primary"
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteBreakdownItem(currentBreakdown.id, task.item_id)}
                    color="error"
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </ListItem>
              ))
            ) : (
              <VuiBox textAlign="center" py={2}>
                <VuiTypography variant="body2" color="text">
                  No checklist items for this breakdown.
                </VuiTypography>
              </VuiBox>
            )}
          </List>
          <TextField
            fullWidth
            variant="outlined"
            label="New Breakdown Item"
            value={newTaskItemText}
            onChange={(e) => setNewTaskItemText(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddBreakdownItem}
            fullWidth
            sx={{ mt: 2 }}
          >
            Add New Breakdown Item
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photos Dialog */}
      <Dialog
        open={pictureDialogOpen}
        onClose={() => setPictureDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{currentBreakdown?.item} Photos</DialogTitle>
        <DialogContent>
          {currentPhotos.length > 0 ? (
            <ImageList>
              {currentPhotos.map((photo) => (
                <ImageListItem key={photo.photo_id}>
                  <img src={photo.url} alt={photo.file_name} />
                  <IconButton
                    color="error"
                    onClick={() => handleDeletePhoto(photo.photo_id)}
                    sx={{ position: "absolute", top: 5, right: 5 }}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </ImageListItem>
              ))}
            </ImageList>
          ) : (
            <VuiBox textAlign="center" py={4}>
              <VuiTypography variant="body2" color="text">
                No photos available for this breakdown.
              </VuiTypography>
            </VuiBox>
          )}
          <VuiBox mt={3}>
            <Button
              variant="contained"
              component="label"
              fullWidth
              color="primary"
              disabled={uploadingPhoto}
              startIcon={<Icon>add_a_photo</Icon>}
            >
              {uploadingPhoto ? "Uploading..." : "Upload Photo"}
              <input
                type="file"
                hidden
                onChange={handleUploadPhoto}
                accept="image/*"
                disabled={uploadingPhoto}
              />
            </Button>
          </VuiBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPictureDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Parts Dialog */}
      <Dialog
        open={partsDialogOpen}
        onClose={() => setPartsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{currentBreakdown?.item} Parts</DialogTitle>
        <DialogContent>
          {currentParts.length > 0 ? (
            <List>
              {currentParts.map((part) => (
                <ListItem key={part.part_id}>
                  {editingPart?.part_id === part.part_id ? (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Part Name"
                          value={editingPart.part_name}
                          onChange={(e) =>
                            setEditingPart({ ...editingPart, part_name: e.target.value })
                          }
                          required
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Part Description"
                          value={editingPart.part_description}
                          onChange={(e) =>
                            setEditingPart({ ...editingPart, part_description: e.target.value })
                          }
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Quantity"
                          value={editingPart.quantity}
                          onChange={(e) =>
                            setEditingPart({ ...editingPart, quantity: e.target.value })
                          }
                          required
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleUpdatePart}
                          startIcon={<Icon>save</Icon>}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={handleCancelEdit}
                          startIcon={<Icon>cancel</Icon>}
                          sx={{ mt: 1 }}
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  ) : (
                    <>
                      <ListItemText
                        primary={part.part_name}
                        secondary={`Quantity: ${part.quantity} - ${part.part_description}`}
                      />
                      <IconButton
                        onClick={() => handleEditPart(part)}
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeletePart(part.part_id)}
                        color="error"
                        size="small"
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </>
                  )}
                </ListItem>
              ))}
            </List>
          ) : (
            <VuiBox textAlign="center" py={4}>
              <VuiTypography variant="body2" color="text">
                No parts available for this breakdown.
              </VuiTypography>
            </VuiBox>
          )}
          <VuiBox mt={3}>
            <TextField
              fullWidth
              label="Part Name"
              margin="dense"
              value={newPart.part_name}
              onChange={(e) => setNewPart({ ...newPart, part_name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Part Description"
              margin="dense"
              value={newPart.part_description}
              onChange={(e) => setNewPart({ ...newPart, part_description: e.target.value })}
            />
            <TextField
              fullWidth
              type="number"
              label="Quantity"
              margin="dense"
              value={newPart.quantity}
              onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddPart}
              fullWidth
              sx={{ mt: 2 }}
            >
              Add Part
            </Button>
          </VuiBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPartsDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog
        open={commentsDialogOpen}
        onClose={() => setCommentsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Breakdown Comments</DialogTitle>
        <DialogContent>
          <List>
            {currentComments.map((comment) => (
              <ListItem key={comment.comment_id}>
                <ListItemText
                  primary={comment.comment_text}
                  secondary={`At ${new Date(comment.created_at).toLocaleString()} By ${
                    comment.created_by
                  }`}
                />
                <IconButton
                  onClick={() => handleDeleteComment(comment.comment_id)}
                  color="error"
                  size="small"
                >
                  <Icon>delete</Icon>
                </IconButton>
              </ListItem>
            ))}
          </List>
          <TextField
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add new comment..."
          />
          <Button variant="contained" color="primary" onClick={handleAddComment} sx={{ mt: 2 }}>
            Add Comment
          </Button>
        </DialogContent>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default BreakdownTracker;
